import React, {
    FormEvent,
    ReactElement,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiExpandAlt } from "react-icons/bi";
import { FiTrash2 } from "react-icons/fi";
import {
    ActionExecuteStartCondition,
    FormField, FormFieldDescriptorInput,
    FormType, GetFormDataDocument, ListRequestTypesDocument,
    WorkflowStep,
    useDeleteFormMutation, useGetExternalFormFieldsQuery, useGetFormDataQuery,
    useGetFormQuery,
    useSaveFormMutation, useSaveWorkflowMutation,
} from "../../../../__generated__/api";
import { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import Confirm from "../../../../components/confirm/Confirm";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Select from "../../../../components/select/Select";
import WorkflowDisplay, { createWorklowStep } from "../../../../components/workflow/Workflow";
import createFormFields, { ExecutorFields } from "../../../../components/workflow/WorkflowFields";
import useArticleContext from "../../../../contexts/HelpContext";
import usePortal from "../../../../contexts/PortalContext";
import useRouting from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/UserContext";
import { ErrorRemote } from "../../../error/Error";
import FormDesigner from "./FormDesigner";

const EXECUTE_CONDITIONS = [
    {
        value: ActionExecuteStartCondition.AfterStarted,
        label: "Assigned Tickets",
    },
    {
        value: ActionExecuteStartCondition.BeforeOrAfterStarted,
        label: "Any ticket",
    },
];

export default function ActionFormSelected({ id }: {id: string}): ReactElement {
    useArticleContext("urn:fq:article:7c8f62a03cbb41bc8a73612c329879f4");

    const { Paths, historyPush } = useRouting();
    const { id: portalId } = usePortal();
    const userSession = useCurrentSession();
    const [isExpandedAutomation, setExpandAutomations] = useState(false);

    const isNew = id === "new";
    const {
        data, error, loading: isLoading,
    } = useGetFormQuery({
        fetchPolicy: "no-cache",
        variables: {
            id: isNew ? "urn:fq:form:new" : id,
            hasId: !isNew,
        },
    });

    const [externalFormId, setExternalFormId] = useState(() => data?.form?.externalFormId);

    const { data: formData, loading: isDataLoading, refetch } = useGetFormDataQuery({
        variables: {
            shouldIncludeTemplateFields: false,
        },
    });
    const { data: externalData } = useGetExternalFormFieldsQuery({
        variables: {
            id: externalFormId,
        },
        skip: externalFormId == null,
    });
    const [saveForm] = useSaveFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [deleteForm] = useDeleteFormMutation();

    const [name, setName] = useState(() => data?.form?.name ?? "");
    const [description, setDescription] = useState(() => data?.form?.description ?? "");
    const [isCreatePrivate, setIsCreatePrivate] = useState(() => data?.form?.isCreatePrivate ?? false);
    const [deflectionIdentity, setDeflectionIdentity] = useState(() => (data?.form?.deflection?.identity
        ?? (data?.form?.deflection?.isEnabled ? portalId : undefined)));
    const [startCondition, setStartCondition] = useState(
        () => data?.form?.actionSettings?.startCondition ?? ActionExecuteStartCondition.AfterStarted,
    );

    const [workflow, setWorkflow] = useState<WorkflowStep[]>(() => data?.form?.workflow?.steps as WorkflowStep[] ?? []);

    const defaultUserGroups = (isNew ? userSession.user?.userGroups?.toIdList("id") : undefined) ?? [];
    const [userGroupIds, setUserGroupIds] = useState(() => data?.form?.userGroupIds as string[]
            ?? defaultUserGroups);

    const [fields, setFields] = useState<FormFieldDescriptorInput[]>(
        () => data?.form?.fields.map(field => removeTypename(field)) ?? [],
    );

    const [displayDelete, setDisplayDelete] = useState(false);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Finished saving Action",
        onSave: (event: FormEvent, onSaved) => {
            event.preventDefault();
            (async () => {
                const workflowVersion = !workflow ? undefined : await saveWorkflow({
                    variables: {
                        id: data?.form?.workflow?.id,
                        workflow: {
                            name: "Auto generated",
                            description: "Auto generated for form",
                            steps: workflow.map(createWorklowStep),
                        },
                    },
                });

                await saveForm({
                    refetchQueries: [
                        { query: GetFormDataDocument },
                        { query: ListRequestTypesDocument },
                    ],
                    variables: {
                        id: id !== "new" ? id : undefined,
                        form: {
                            type: FormType.Action,
                            fields: fields.map(field => ({
                                ...field,
                                __typename: undefined,
                            })),
                            name,
                            description,
                            isCreatePrivate,
                            userGroupIds,
                            workflowId: workflowVersion?.data?.saveWorkflow?.id,
                            deflection: {
                                isEnabled: deflectionIdentity != null,
                                identity: deflectionIdentity,
                            },
                            externalFormId,
                            actionSettings: {
                                startCondition,
                            },

                        },
                    },
                });
                onSaved();
                setTimeout(() => {
                    historyPush(Paths.ADMINISTRATOR_SETTING, {
                        setting: "workflow-actions",
                    });
                });
            })();
        },
    });

    dirtyTracker.useWatchProperty(name, data?.form?.name || "");
    dirtyTracker.useWatchProperty(description, data?.form?.description || "");
    dirtyTracker.useWatchProperty(isCreatePrivate, data?.form?.isCreatePrivate ?? false);
    dirtyTracker.useWatchProperty(externalFormId, data?.form?.externalFormId);
    dirtyTracker.useWatchProperty(userGroupIds.join(), (data?.form?.userGroupIds ?? defaultUserGroups).join());
    dirtyTracker.useWatchProperty(
        startCondition,
        data?.form?.actionSettings?.startCondition ?? ActionExecuteStartCondition.AfterStarted,
    );

    dirtyTracker.useLoad(() => {
        setName(data?.form?.name ?? "");
        setDescription(data?.form?.description ?? "");
        setFields(data?.form?.fields.map(field => removeTypename(field)) ?? []);
        setUserGroupIds(data?.form?.userGroupIds ?? defaultUserGroups);
        setIsCreatePrivate(data?.form?.isCreatePrivate ?? false);
        setDeflectionIdentity(data?.form?.deflection?.identity
            ?? (data?.form?.deflection?.isEnabled ? portalId : undefined));
        setWorkflow(data?.form?.workflow?.steps as WorkflowStep[] ?? []);
        setExternalFormId(data?.form?.externalFormId);
        setStartCondition(data?.form?.actionSettings?.startCondition ?? ActionExecuteStartCondition.AfterStarted);
    }, [data?.form]);

    if (isLoading || isDataLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const onDelete = async () => {
        await deleteForm({
            refetchQueries: [
                { query: GetFormDataDocument },
            ],
            variables: {
                id,
            },
        });
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "workflow-actions",
        });
    };

    const fieldMap = formData?.formFields.toIdMap("id") ?? {};

    const formFields = (externalData?.externalFormFields as FormField[]
        ?? fields.mapFilter(row => (row.fieldId ? fieldMap[row.fieldId] : undefined)));

    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the action:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>
            <div>
                <div className="mb-4 d-flex">
                    <div className="col border-end me-3 pe-3">
                        <form className="border p-3 rounded mb-4 bg-white">

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setName(evt.target.value)}
                                    value={name}
                                />
                                <Form.Text>
                                    The name of the action displayed at the top and in the action selection.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Description
                                    {" "}
                                    <small className="text-light">(Optional)</small>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    as="textarea"
                                    placeholder="This action is used for ..."
                                    onChange={evt => setDescription(evt.target.value)}
                                    value={description}
                                />
                                <Form.Text>
                                    The optional description is to help you understand where this action
                                    is used. It will never be displayed to the end user.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Groups
                                </Form.Label>
                                <UserGroups
                                    isMulti
                                    canCreate
                                    value={userGroupIds}
                                    onSelected={ids => setUserGroupIds(ids)}
                                    canSelectNone
                                />
                                <Form.Text>
                                    Which user groups are allowed to execute this action?
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    When to execute
                                </Form.Label>
                                <Select
                                    placeholder="Tickets assigned to you."
                                    onChange={value => setStartCondition(value.value as ActionExecuteStartCondition)}
                                    value={EXECUTE_CONDITIONS.find(condition => condition.value === startCondition)}
                                    options={EXECUTE_CONDITIONS}
                                />
                                <Form.Text>
                                    When can this action be executed? Only after the ticket has assigned, or to any ticket
                                    in any state, before or after assignment?
                                </Form.Text>
                            </Form.Group>

                            {dirtyTracker.saveComponent}

                        </form>

                        <div className="border p-3 rounded mb-4 bg-white">
                            <div className="d-flex justify-content-between w-100 align-items-center mb-2 ">
                                <h5 className="text-light mb-0">
                                    Automations
                                </h5>
                                <button
                                    type="button"
                                    onClick={() => setExpandAutomations(true)}
                                    className="btn btn-outline px-2 py-1 text-light p-1"
                                >
                                    <BiExpandAlt />
                                </button>
                            </div>
                            <div className="mb-5">
                                Automation allows you to create logic, trigger services, and take actions on this requests.
                            </div>

                            <WorkflowDisplay
                                value={workflow}
                                onChange={updated => {
                                    setWorkflow(updated);
                                    dirtyTracker.dirty();
                                }}
                                input={createFormFields(formFields, ExecutorFields.CAN_SET)}
                                isModal={isExpandedAutomation}
                                onHideModal={() => setExpandAutomations(false)}
                            />

                        </div>

                    </div>
                    <div className="d-flex flex-column col-4 ">
                        <div className="mb-4">
                            <FormDesigner
                                fields={fields}
                                formData={formData}
                                formName={name}
                                groupIds={userGroupIds}
                                id={id}
                                onCustomFieldAdded={refetch}
                                setFields={value => {
                                    setFields(value);
                                    dirtyTracker.dirty();
                                }}
                                requestFormType="action"
                            />
                        </div>
                        {id !== "new" && (
                            <div className="border p-3 mt-3 rounded bg-white align-items-center d-flex justify-content-between">
                                <strong className="d-block ">Actions</strong>
                                <div className="d-flex">

                                    <button
                                        type="button"
                                        className="d-block btn btn-none text-gray-500  py-0 pe-0"
                                        onClick={() => setDisplayDelete(true)}
                                    >
                                        <FiTrash2 />
                                        {" "}
                                        Delete
                                    </button>
                                </div>

                            </div>

                        )}
                    </div>
                </div>

            </div>
        </>
    );
}
